import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns'

import type { DateRange, DateRangePreset } from './types'

export const MAX_DATE_RANGE_DAYS = 366

export function isWithinMaxDateRange(range: { from: Date; to: Date }) {
  return (
    differenceInCalendarDays(range.to, range.from) + 1 <= MAX_DATE_RANGE_DAYS
  )
}

export function getRangeEndBounds(start: Date) {
  return {
    before: subDays(start, MAX_DATE_RANGE_DAYS - 1),
    after: addDays(start, MAX_DATE_RANGE_DAYS - 1),
  }
}

export function clampToMaxDateRange<T extends { from: Date; to: Date }>(
  range: T,
): T {
  if (isWithinMaxDateRange(range)) return range
  return {
    ...range,
    from: startOfDay(subDays(range.to, MAX_DATE_RANGE_DAYS - 1)),
  }
}

export function readCustomDateRange(
  from: string | null,
  to: string | null,
): DateRange | null {
  if (!from || !to) return null
  const range = {
    from: new Date(from),
    to: new Date(to),
    preset: 'custom' as const,
  }
  if (Number.isNaN(range.from.getTime()) || Number.isNaN(range.to.getTime()))
    return null
  if (range.from > range.to) return null
  return clampToMaxDateRange(range)
}

/**
 * Every preset the shared date-range control offers, `custom` included. Kept
 * beside `getPresetRange` because both screens that read a range off the URL
 * have to reject anything outside this list before calling it.
 */
export const DATE_RANGE_PRESETS = [
  'today',
  'last-7-days',
  'last-30-days',
  'this-month',
  'last-month',
  '3m',
  '6m',
  '12m',
  'custom',
] as const satisfies ReadonlyArray<DateRangePreset>

export function isDateRangePreset(value: string): value is DateRangePreset {
  return (DATE_RANGE_PRESETS as ReadonlyArray<string>).includes(value)
}

export function getPresetRange(
  preset: DateRangePreset,
  now: Date = new Date(),
): {
  from: Date
  to: Date
} {
  switch (preset) {
    case 'today':
      return { from: startOfDay(now), to: now }
    case 'last-7-days':
      return { from: subDays(now, 7), to: now }
    case 'last-30-days':
      return { from: subDays(now, 30), to: now }
    case 'this-month':
      return { from: startOfMonth(now), to: now }
    case 'last-month': {
      const prev = subMonths(now, 1)
      return { from: startOfMonth(prev), to: endOfMonth(prev) }
    }
    // N whole calendar months ENDING ON THE CURRENT ONE, which is what the labels
    // ("Last 6 months") say. Ending on the last complete month instead hides everything
    // that happened this month, so a range picked today can come back empty.
    case '3m':
      return { from: subMonths(startOfMonth(now), 2), to: now }
    case '6m':
      return { from: subMonths(startOfMonth(now), 5), to: now }
    case '12m':
      return { from: subMonths(startOfMonth(now), 11), to: now }

    default:
      return { from: subDays(now, 30), to: now }
  }
}

export { DATE_FNS_LOCALES } from '@/lib/intl/date-locales'
